import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Clock, AlertTriangle, FileText, Scale, Phone } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DismissalDeadlineCalculator from "../components/DismissalDeadlineCalculator";
import ContactSection from "../components/ContactSection";
import { useSEO, useJsonLd, SITE } from "../lib/seo";

const PATH = "/calcolo-termini-impugnazione-licenziamento";
const H1 = "Calcolo termini per l'impugnazione del licenziamento";
const META_TITLE = "Calcolo termini impugnazione licenziamento — 60 e 180 giorni";
const META_DESCRIPTION =
    "Strumento gratuito per calcolare i termini di legge (60 e 180 giorni, art. 6 L. 604/1966) per impugnare un licenziamento. Inserisci la data di notifica e verifica la scadenza.";

const FAQS = [
    {
        q: "Quanti giorni ho per impugnare un licenziamento?",
        a: "Hai 60 giorni dalla ricezione del licenziamento per l'impugnazione stragiudiziale (raccomandata A/R o PEC). Successivamente hai altri 180 giorni per depositare il ricorso al Tribunale del Lavoro o attivare la conciliazione. Entrambi sono termini di decadenza previsti dall'art. 6 L. 604/1966.",
    },
    {
        q: "Da quando decorrono i 60 giorni per impugnare?",
        a: "Il termine di 60 giorni decorre dal ricevimento della lettera di licenziamento, e non dalla data di spedizione da parte del datore. In caso di licenziamento comunicato in forma scritta ma senza motivazione contestuale, il termine può decorrere dalla comunicazione delle motivazioni.",
    },
    {
        q: "Cosa succede se non impugno entro 60 giorni?",
        a: "Il licenziamento diventa definitivamente inoppugnabile: si perde il diritto di contestarne l'illegittimità e di ottenere la reintegrazione o l'indennità risarcitoria. È un termine di decadenza, non di prescrizione, quindi non ammette interruzione. Rivolgiti tempestivamente a un avvocato giuslavorista.",
    },
    {
        q: "Come si impugna un licenziamento nel modo corretto?",
        a: "L'impugnazione stragiudiziale deve essere fatta con atto scritto che manifesti la volontà di contestare il licenziamento (anche solo per iscritto, senza formule sacramentali). Deve essere trasmessa entro 60 giorni via raccomandata A/R o PEC al datore di lavoro. Successivamente occorre depositare il ricorso al Tribunale entro ulteriori 180 giorni.",
    },
    {
        q: "Il calcolo dei termini si fa in giorni di calendario o lavorativi?",
        a: "In giorni di calendario, non lavorativi. Sono compresi sabati, domeniche e festivi. Se il termine scade in giorno festivo, viene generalmente prorogato al primo giorno feriale successivo (art. 2963 c.c.).",
    },
    {
        q: "Vale anche per licenziamento durante il periodo di prova o per dimissioni?",
        a: "I termini di 60 + 180 giorni si applicano a tutti i licenziamenti, incluso quello del lavoratore in prova (se contestabile) e in caso di risoluzione del datore di lavoro. Per le dimissioni per giusta causa e la loro impugnazione i termini possono differire: valuta il caso con un avvocato.",
    },
];

export default function CalculatorPage() {
    useSEO({
        title: META_TITLE,
        description: META_DESCRIPTION,
        path: PATH,
    });

    useJsonLd(
        [
            {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE.url}/` },
                    { "@type": "ListItem", "position": 2, "name": H1, "item": `${SITE.url}${PATH}` },
                ],
            },
            {
                "@context": "https://schema.org",
                "@type": "WebApplication",
                "name": "Calcolo termini impugnazione licenziamento",
                "url": `${SITE.url}${PATH}`,
                "applicationCategory": "LegalService",
                "operatingSystem": "Web",
                "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" },
                "description": META_DESCRIPTION,
                "inLanguage": "it-IT",
                "publisher": { "@id": `${SITE.url}/#legalservice` },
            },
            {
                "@context": "https://schema.org",
                "@type": "HowTo",
                "name": "Come calcolare i termini per impugnare un licenziamento",
                "description":
                    "Guida passo-passo per calcolare i termini decadenziali di 60 e 180 giorni previsti dall'art. 6 L. 604/1966.",
                "step": [
                    {
                        "@type": "HowToStep",
                        "position": 1,
                        "name": "Individua la data di ricezione",
                        "text":
                            "Verifica sulla ricevuta della raccomandata o della PEC quando hai ricevuto la lettera di licenziamento. Da quel giorno decorre il termine.",
                    },
                    {
                        "@type": "HowToStep",
                        "position": 2,
                        "name": "Calcola i 60 giorni per l'impugnazione stragiudiziale",
                        "text":
                            "Aggiungi 60 giorni di calendario alla data di ricezione. Entro tale data devi trasmettere al datore, via raccomandata A/R o PEC, un atto scritto di impugnazione.",
                    },
                    {
                        "@type": "HowToStep",
                        "position": 3,
                        "name": "Calcola i 180 giorni per il deposito del ricorso",
                        "text":
                            "Dal giorno di invio dell'impugnazione stragiudiziale hai ulteriori 180 giorni per depositare il ricorso al Tribunale del Lavoro o attivare la conciliazione presso l'ITL.",
                    },
                    {
                        "@type": "HowToStep",
                        "position": 4,
                        "name": "Rivolgiti a un avvocato giuslavorista",
                        "text":
                            "I termini sono decadenziali: se saltano, il diritto di impugnazione si estingue. Contatta lo Studio per una prima consulenza gratuita e per la corretta gestione della pratica.",
                    },
                ],
            },
            {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": FAQS.map((f) => ({
                    "@type": "Question",
                    "name": f.q,
                    "acceptedAnswer": { "@type": "Answer", "text": f.a },
                })),
            },
        ],
        "calculator-jsonld",
    );

    return (
        <div data-testid="calculator-page">
            <Header />
            <main>
                {/* Hero */}
                <section className="relative pt-40 pb-16 lg:pt-48 lg:pb-24 bg-navy overflow-hidden">
                    <div className="absolute top-0 bottom-0 left-10 lg:left-24 w-px bg-gold/25 hidden md:block" />
                    <div className="max-w-5xl mx-auto px-6 lg:px-10 relative z-10">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="block w-16 h-[2px] bg-gold" />
                            <span className="label-eyebrow !text-gold">Strumento gratuito</span>
                        </div>
                        <h1 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6 max-w-4xl">
                            Calcolo termini per l&apos;impugnazione del <span className="italic text-gold">licenziamento</span>
                        </h1>
                        <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl">
                            Verifica in pochi secondi la scadenza dei termini di 60 e 180 giorni previsti dall&apos;art. 6 L. 604/1966
                            per l&apos;impugnazione del licenziamento e il deposito del ricorso al Tribunale del Lavoro.
                        </p>
                    </div>
                </section>

                {/* Calculator */}
                <DismissalDeadlineCalculator />

                {/* Normativa & guida */}
                <section className="py-24 lg:py-32 bg-white">
                    <div className="max-w-5xl mx-auto px-6 lg:px-10">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="gold-rule" />
                            <span className="label-eyebrow">Come funziona</span>
                        </div>
                        <h2 className="font-serif text-navy text-3xl md:text-4xl leading-[1.15] mb-12 max-w-3xl">
                            I termini di decadenza per impugnare il licenziamento
                        </h2>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
                            <div className="border-l-4 border-gold pl-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <Clock size={20} className="text-gold-deep" />
                                    <h3 className="font-serif text-navy text-2xl">1. Impugnazione — 60 giorni</h3>
                                </div>
                                <p className="text-slate-700 leading-relaxed">
                                    Dalla ricezione della lettera di licenziamento il lavoratore ha <strong>60 giorni</strong> per
                                    contestarne la legittimità con qualsiasi atto scritto, anche stragiudiziale, trasmesso via
                                    raccomandata A/R o PEC. È un termine di decadenza: se saltato, il licenziamento diventa
                                    inoppugnabile.
                                </p>
                            </div>
                            <div className="border-l-4 border-gold pl-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <Scale size={20} className="text-gold-deep" />
                                    <h3 className="font-serif text-navy text-2xl">2. Ricorso — 180 giorni</h3>
                                </div>
                                <p className="text-slate-700 leading-relaxed">
                                    Dopo l&apos;impugnazione stragiudiziale, restano <strong>180 giorni</strong> per depositare il
                                    ricorso al Tribunale del Lavoro o per attivare la conciliazione presso l&apos;Ispettorato o le
                                    sedi sindacali. Anche questo termine è perentorio.
                                </p>
                            </div>
                        </div>

                        <div className="bg-slate-50 border border-slate-200 p-8 lg:p-10">
                            <div className="flex items-start gap-4">
                                <AlertTriangle size={24} className="text-gold-deep flex-shrink-0 mt-1" />
                                <div>
                                    <h3 className="font-serif text-navy text-2xl mb-3">
                                        Riferimenti normativi
                                    </h3>
                                    <ul className="space-y-2 text-slate-700 leading-relaxed">
                                        <li className="flex items-start gap-2">
                                            <Check size={16} className="text-gold-deep mt-1 flex-shrink-0" />
                                            <span><strong>Art. 6 L. 604/1966</strong> — disciplina il termine di 60 giorni per l&apos;impugnazione e il successivo termine di 180 giorni per il deposito del ricorso.</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check size={16} className="text-gold-deep mt-1 flex-shrink-0" />
                                            <span><strong>Art. 32 L. 183/2010 (Collegato Lavoro)</strong> — ha introdotto il doppio termine attualmente in vigore, sostituendo la disciplina previgente.</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check size={16} className="text-gold-deep mt-1 flex-shrink-0" />
                                            <span><strong>Rito Fornero (L. 92/2012)</strong> e <strong>rito ordinario del lavoro</strong> (artt. 409 e ss. c.p.c.) — regolamentano il procedimento davanti al Tribunale.</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Cross-link */}
                        <div className="mt-10 flex items-center gap-4 flex-wrap">
                            <span className="label-eyebrow">Vedi anche:</span>
                            <Link
                                to="/valutatore-rischio-licenziamento"
                                className="inline-flex items-center gap-2 text-navy hover:text-gold-deep transition-colors text-sm font-semibold border-b border-gold pb-0.5"
                            >
                                Valutatore rischio economico del licenziamento
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="py-24 lg:py-32 bg-[#FAFAFA]">
                    <div className="max-w-5xl mx-auto px-6 lg:px-10">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="gold-rule" />
                            <span className="label-eyebrow">Domande frequenti</span>
                        </div>
                        <h2 className="font-serif text-navy text-3xl md:text-4xl leading-[1.15] mb-12 max-w-3xl">
                            FAQ sui termini per impugnare il licenziamento
                        </h2>
                        <div className="divide-y divide-slate-200 border-y border-slate-200">
                            {FAQS.map((f, i) => (
                                <details key={i} className="group py-6" data-testid={`calc-faq-${i}`}>
                                    <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                                        <h3 className="font-serif text-navy text-xl md:text-2xl leading-tight group-hover:text-gold-deep transition-colors">
                                            {f.q}
                                        </h3>
                                        <span className="flex-shrink-0 w-10 h-10 border border-navy/30 group-hover:border-gold group-hover:bg-gold flex items-center justify-center transition-all font-serif text-xl text-navy group-open:rotate-45">
                                            +
                                        </span>
                                    </summary>
                                    <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed pr-16">
                                        {f.a}
                                    </p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-20 bg-navy">
                    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                        <FileText size={32} className="text-gold mx-auto mb-6" />
                        <h2 className="font-serif text-white text-3xl md:text-4xl leading-tight mb-6">
                            Hai bisogno di assistenza per impugnare un licenziamento?
                        </h2>
                        <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
                            La prima consulenza è sempre <strong className="text-white">gratuita</strong>. Contatta lo Studio
                            per una valutazione tempestiva del tuo caso.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link
                                to="/#contatti"
                                data-testid="calc-page-cta"
                                className="btn-gold group"
                            >
                                Richiedi consulenza
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                            <a
                                href="tel:0523468737"
                                className="inline-flex items-center gap-2 px-7 py-3 border border-white/30 text-white font-semibold tracking-wide text-sm transition-all duration-300 hover:border-gold hover:text-gold"
                            >
                                <Phone size={16} /> 0523 468737
                            </a>
                        </div>
                    </div>
                </section>

                <ContactSection />
            </main>
            <Footer />
        </div>
    );
}
